import 'features/rate-upload/components/rate-upload-button.scss';
import { CustomTooltip } from '@/components/custom-tooltip';
import { IconButton } from '@mui/material';
import styles from 'common/_variables.module.scss';
import { usePropertyContext } from 'context/propertyContext';
import type { PropMetrics } from 'features/home/types/prop-metrics';
import { RateUploadButton } from 'features/rate-upload/components/rate-upload-button';
import { ResetRateModal } from 'features/rate-upload/components/reset-rate-modal';
import { useRateUpload } from 'features/rate-upload/hooks/use-rate-upload';
import {
	selectPolling,
	selectUploading,
} from 'features/rate-upload/redux/rate-upload-selectors';
import { useUser } from 'features/users/context/userContext';
import { labels } from 'locales/en.label';
import { useEffect, useMemo, useState } from 'react';
import { MdRefresh } from 'react-icons/md';
import { useAppSelector } from 'redux/hooks';
import { RecReviewStatus } from 'types/RecReviewTypes';

type RateOverrideFieldProps = {
	metrics: PropMetrics;
	setDirty?: (isDirty: boolean) => void;
};

export const RateOverrideField = ({
	metrics,
	setDirty,
}: RateOverrideFieldProps) => {
	const { isReadOnly } = useUser();
	const { handleChangeSingleRate, handleResetSingleRate, handleTriggerUpload } =
		useRateUpload();
	const polling = useAppSelector(selectPolling);
	const uploading = useAppSelector(selectUploading);
	const { currencySymbol } = usePropertyContext();

	const recRate = useMemo(() => metrics.rec_rate, [metrics.rec_rate]);
	const recStatus = useMemo(() => metrics.rec_status, [metrics.rec_status]);
	const [fieldValue, setFieldValue] = useState('');
	const [prefix, setPrefix] = useState(currencySymbol);
	const [showResetModal, setShowResetModal] = useState(false);

	useEffect(() => {
		if (metrics.override_rate) {
			setFieldValue(
				`${Number.parseFloat(metrics.override_rate.toString()).toFixed(2)}`,
			);
		}
	}, [metrics.override_rate]);

	const didUserOverride = recRate !== metrics.override_rate;
	const textValue = useMemo(
		() => `${prefix}${fieldValue}`,
		[prefix, fieldValue],
	);

	const handleBlur = () => {
		const formattedRate = Number.parseFloat(fieldValue);
		setFieldValue(formattedRate.toFixed(2));
		handleChangeSingleRate({
			...metrics,
			override_rate: formattedRate,
			rec_status: RecReviewStatus.PENDING,
		});
		setPrefix(currencySymbol);
	};

	const handleRateChange = (rateText: string) => {
		if (rateText === '') rateText = '0';
		if (rateText.length > 1 && rateText[0] === '0')
			rateText = rateText.slice(1);

		const validNumber = new RegExp(/^\d*\.?\d*$/);
		if (!validNumber.test(rateText)) return;

		setDirty?.(true);
		setFieldValue(rateText);
	};

	const handleConfirmReset = (e: any) => {
		e?.stopPropagation();
		setShowResetModal(false);
		const originalValue = fieldValue;
		setFieldValue(recRate.toFixed(2));
		handleResetSingleRate(metrics.stay_date).then((res) => {
			if (res.errors) setFieldValue(originalValue);
		});
	};

	const closeResetModal = (e: any) => {
		e?.stopPropagation();
		setShowResetModal(false);
	};
	return (
		<span className='rate-override-field'>
			<ResetRateModal
				isShown={showResetModal}
				hide={closeResetModal}
				onCancel={closeResetModal}
				onConfirm={handleConfirmReset}
			/>
			<div className='tw-flex tw-justify-between'>
				<div className='tw-flex tw-items-center'>
					{recStatus === RecReviewStatus.PENDING && (
						<>
							<RateUploadButton
								disabled={polling || uploading || isReadOnly}
								handleTriggerUpload={handleTriggerUpload}
								polling={polling}
								uploading={uploading}
							/>
							<CustomTooltip content={<p>{labels.home.tooltip.reset}</p>}>
								<IconButton
									disableFocusRipple
									disableRipple
									aria-label='reset'
									onClick={(e) => {
										e.stopPropagation();
										setShowResetModal(true);
									}}
									disabled={!didUserOverride || isReadOnly}
								>
									<MdRefresh />
								</IconButton>
							</CustomTooltip>
						</>
					)}

					<input
						type='text'
						className='override-input'
						disabled={isReadOnly}
						style={{
							marginLeft: '4px',
							...(didUserOverride && { color: styles.colorBlue }),
						}}
						value={textValue}
						onClick={(e) => {
							e.stopPropagation();
							const inputElement = e.target as HTMLInputElement;
							inputElement.select();
						}}
						onFocus={() => setPrefix('')}
						onBlur={(e) => {
							e.stopPropagation();
							handleBlur();
						}}
						onChange={(e) => {
							e.stopPropagation();
							handleRateChange(e.target.value);
						}}
					/>
				</div>
			</div>
		</span>
	);
};
